import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';
import { GlassButton } from '../../../components/ui';
import { TEXT_COLORS, BORDER_COLORS, BG_COLORS } from '../../../utils/colors';

/** Сортировка по умолчанию — как на страницах-списках (`WikiItemGrid`). */
export const SEARCH_DEFAULT_SORT = 'name';

/** Значения, которые понимает эндпоинт (и которые есть в выпадашке). */
export const SEARCH_SORT_VALUES = ['name', 'name_desc'] as const;

/**
 * Значение сортировки из адреса — с проверкой. В `?sortBy=` может лежать что угодно:
 * старая ссылка, руками правленый адрес. Без проверки такое значение уходит в `Select`,
 * пункта с ним нет — и поле «Сортировка» рисуется пустым.
 */
export const normalizeSearchSort = (value: string | null): string =>
  SEARCH_SORT_VALUES.includes(value as typeof SEARCH_SORT_VALUES[number])
    ? (value as string)
    : SEARCH_DEFAULT_SORT;

interface WikiSearchBarProps {
  /** Вызывается при сабмите формы (Enter), когда в поле >= MIN_LEN символов (уже trim). */
  onSearch: (query: string) => void;
  /** Очистка поля, сортировки и результатов. */
  onClear: () => void;
  /** Начальный запрос (например, из URL ?q=). */
  initialQuery?: string;
  /** Текущая сортировка: `name` | `name_desc`. */
  sortBy: string;
  /** Смена сортировки — применяется сразу, как в фильтрах страниц-списков. */
  onSortChange: (value: string) => void;
}

const MIN_LEN = 2;

/**
 * Строка глобального поиска по вики — сделана ОДИН В ОДИН со строкой фильтров
 * страниц-списков (`WikiItemGrid`):
 * - десктоп: поиск (Enter) + «Сортировка» + «Очистить»;
 * - мобила: иконка `Tune` слева от поля открывает нижний drawer с сортировкой и
 *   парой «Очистить / Применить»; отдельной кнопки «Очистить» в строке НЕТ.
 */
const WikiSearchBar: React.FC<WikiSearchBarProps> = React.memo(({
  onSearch,
  onClear,
  initialQuery,
  sortBy,
  onSortChange,
}) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [value, setValue] = useState(initialQuery ?? '');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  // Пары «поле ↑ / поле ↓», как в фильтрах списков; бэкенд знает только name | name_desc
  const sortOptions = [
    { value: 'name', label: `${t('filters.sortName')} ↑` },
    { value: 'name_desc', label: `${t('filters.sortName')} ↓` },
  ];

  const submit = useCallback(() => {
    const q = value.trim();
    if (q.length >= MIN_LEN) onSearch(q);
  }, [value, onSearch]);

  const handleClear = useCallback(() => {
    setValue('');
    setFiltersDrawerOpen(false);
    onClear();
  }, [onClear]);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: { xs: 'nowrap', sm: 'wrap' }, flexShrink: 0, pt: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 2 } }}>
        {/* Кнопка фильтров — только мобила */}
        {!isSm && (
          <Box
            onClick={() => setFiltersDrawerOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent',
              border: `1px solid ${BORDER_COLORS.LIGHT}`,
              borderRadius: '4px',
              px: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: BG_COLORS.GLASS,
                borderColor: BORDER_COLORS.LIGHTER,
              },
            }}
          >
            <TuneIcon sx={{ color: TEXT_COLORS.MUTED, fontSize: '1rem' }} />
          </Box>
        )}

        {/* Search — как в WikiItemGrid */}
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          sx={{ flex: 1 }}
        >
          <TextField
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('search.placeholder')}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: TEXT_COLORS.MUTED }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
          />
        </Box>

        {/* Сортировка — только десктоп */}
        {isSm && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('filters.sort')}</InputLabel>
            <Select
              value={sortBy}
              label={t('filters.sort')}
              onChange={(e) => onSortChange(e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Очистить — только десктоп; на мобиле она внутри drawer'а */}
        {isSm && (
          <GlassButton
            glassVariant="secondary"
            size="small"
            onClick={handleClear}
            sx={{ minHeight: 0, py: '5px', px: 1.5, fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, flexShrink: 0, whiteSpace: 'nowrap', backdropFilter: 'none', '&:hover': { boxShadow: 'none', borderColor: BORDER_COLORS.FOCUS } }}
          >
            <ClearIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {t('filters.clear')}
          </GlassButton>
        )}
      </Box>

      {/* Мобильный drawer с сортировкой — разметка 1:1 с WikiItemGrid */}
      <Drawer
        anchor="bottom"
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        PaperProps={{
          sx: {
            background: BG_COLORS.CARD,
            backdropFilter: 'blur(10px)',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            maxHeight: '80vh',
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            borderBottom: 'none',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            pb: 1,
            borderBottom: `1px solid ${BORDER_COLORS.DEFAULT}`,
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_COLORS.PRIMARY }}>
              {t('filters.title')}
            </Typography>
            <IconButton onClick={() => setFiltersDrawerOpen(false)} size="small">
              <CloseIcon sx={{ color: TEXT_COLORS.MUTED }} />
            </IconButton>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
              {t('filters.sort')}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                sx={{ '& .MuiSelect-select': { py: 1 } }}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <GlassButton
              fullWidth
              glassVariant="secondary"
              onClick={handleClear}
            >
              {t('filters.clear')}
            </GlassButton>
            <GlassButton
              fullWidth
              onClick={() => {
                setFiltersDrawerOpen(false);
                submit();
              }}
            >
              {t('filters.apply')}
            </GlassButton>
          </Box>
        </Box>
      </Drawer>
    </>
  );
});

WikiSearchBar.displayName = 'WikiSearchBar';

export default WikiSearchBar;
